import {
	ReactElement,
	useEffect,
	useCallback,
	useState,
	useMemo
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import {
	ContextActionBar,
	Dialog,
	ImageViewer,
	Toolbar
} from "client/Components";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import styles from "./ImagePreviewDialog.module.less";
import { Key } from "client/Misc/commonTypes";
import { Action } from "client_legacy/ndaw/actions";
import { useOpenDialog } from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

export interface ImageFile {
	id: number | string;
	name: string;
	url: string;
};

export type ImagePreviewDialogProps<TContext> = {
	onClose: () => void;
	currentImage: ImageFile;
	context: TContext | undefined;
	actions: readonly Action<TContext>[];
} & (
	{
		allImages: readonly ImageFile[];
		onActiveImageChanged: (file: ImageFile) => void;
	} | {
		allImages?: undefined;
		onActiveImageChanged?: undefined;
	}
);

export const ImagePreviewDialog = <TContext,>(props: ImagePreviewDialogProps<TContext>): ReactElement => {
	const {
		onClose,
		currentImage,
		allImages,
		onActiveImageChanged,
		context,
		actions = EMPTY_ARRAY,
	} = props;

	const [isLoading, setIsLoading] = useState<boolean>(true);

	const currentImageIndex: number = useMemo(
		(): number => {
			return isNotNil(allImages)
				? allImages.findIndex((i: ImageFile): boolean => i.id === currentImage.id)
				: -1;
		},
		[allImages, currentImage.id]
	);

	const nextImage: ImageFile | undefined = (
		isNotNil(allImages)
			&& currentImageIndex !== -1
			&& currentImageIndex + 1 < allImages.length
	)
		? allImages[currentImageIndex + 1]
		: undefined;

	const previousImage: ImageFile | undefined = (
		isNotNil(allImages)
			&& currentImageIndex !== -1
			&& currentImageIndex > 0
	)
		? allImages[currentImageIndex - 1]
		: undefined;

	useEffect(
		(): EffectWithDestructorResult => {
			async function preloadImages(signal: AbortSignal): Promise<void> {
				setIsLoading(true);

				try {
					await preloadImage(currentImage.url);

					signal.throwIfAborted();

					if (nextImage) {
						preloadImage(nextImage.url);
					}

					if (previousImage) {
						preloadImage(previousImage.url);
					}

					setIsLoading(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setIsLoading(false);
				}
			}

			const cancellation = new AbortController();

			preloadImages(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			currentImage,
			nextImage,
			previousImage
		]
	);

	const nextHandler = useCallback(
		(): void => {
			if (isNotNil(nextImage)) {
				ensureNotNil(onActiveImageChanged)(nextImage);
			}
		},
		[nextImage, onActiveImageChanged]
	);

	const previousHandler = useCallback(
		(): void => {
			if (isNotNil(previousImage)) {
				ensureNotNil(onActiveImageChanged)(previousImage);
			}
		},
		[onActiveImageChanged, previousImage]
	);

	useEffect(
		(): (() => void) => {
			const handleKeyDown = (event: KeyboardEvent): void => {
				const key: Key | null = tryResolveKeyboardKey(event);
				switch (key) {
					case Key.ArrowRight:
						nextHandler();
						break;
					case Key.ArrowLeft:
						previousHandler();
						break;
					default:
						// nothing to do
						break;
				};
			};

			window.addEventListener("keydown", handleKeyDown);

			return () => {
				window.removeEventListener("keydown", handleKeyDown);
			};
		},
		[nextHandler, previousHandler]
	);

	return (
		<Dialog
			trackingId="image-preview-dialog"
			size="large"
			isEnlargeable
		>
			<Dialog.Header
				title={currentImage.name}
				onClose={onClose}
			/>
			<Dialog.Body goesEdgeToEdge>
				{typeof context !== "undefined" && (
					<ContextActionBar
						actions={actions}
						context={context}
						trackingId="action-bar"
					/>
				)}
				<div className={styles.content}>
					<ImageViewer
						isLoading={isLoading}
						imageUrl={currentImage.url}
						isBackgroundToggleAvailable
						trackingId="image-viewer"
					/>
				</div>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					{isNotNil(allImages) && (
						<Toolbar.Button
							kind="primary"
							text={localize("_Previous")}
							onClick={previousHandler}
							trackingId="previous"
							isDisabled={!previousImage}
						/>
					)}
					{isNotNil(allImages) && (
						<Toolbar.Button
							kind="primary"
							text={localize("_Next")}
							onClick={nextHandler}
							trackingId="next"
							isDisabled={!nextImage}
						/>
					)}
					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

ImagePreviewDialog.displayName = "ImagePreviewDialog";

type ImagePreviewDialogInitialData<TContext> = OmitDistributive<ImagePreviewDialogProps<TContext>, "onClose">;

export function useImagePreviewDialog<TContext>(): [
	element: ReactElement | null,
	open: (props: ImagePreviewDialogInitialData<TContext>) => Promise<void>
] {
	return useOpenDialog(
		(props, onClose) => (
			<ImagePreviewDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}

function preloadImage(imageSource: string): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve();
		img.onerror = img.onabort = () => reject();
		img.src = imageSource;
	});
}
